import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/dataStoreItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/dataStoreItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/dataStoreItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/dataStoreItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/dataStoreItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/dataStoreItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/dataStoreItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/dataStoreItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/dataStoreItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realhub/dataStoreItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/dataStoreItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/dataStoreItem/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  loaded: false,
  loadedForEntities: [],
  loading: false,
  errors: [],
  updating: false,
  deleting: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedForEntities: payload.loadedForEntities,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators
export function loadDataStoreItems(options){
  const entityKey = options.entityKey || `${options.owner_type}${options.owner_id}`

  return (dispatch, getState) => {
    const { dataStoreItems } = getState()
    const loadedForEntities = [...dataStoreItems.loadedForEntities]

    if (entityKey && !loadedForEntities.includes(entityKey)){
      loadedForEntities.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForEntities: loadedForEntities || null }))

    const promise = api('/agency/data_store_items.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DATA_STORE_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createDataStoreItem(dataStoreItem, options){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      behaviour: dataStoreItem.behaviour,
      data_store_item: dataStoreItem,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/agency/data_store_items.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DATA_STORE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateDataStoreItem(dataStoreItem, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      data_store_item: dataStoreItem,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agency/data_store_items/${dataStoreItem.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DATA_STORE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function deleteDataStoreItem(dataStoreItem){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/agency/data_store_items/${dataStoreItem.id}.json`, {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DATA_STORE_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))

        return { success: false, result: errors }
      })
    return promise
  }
}

export function updateItemsForGroupKey(params, updateParams){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      ...params,
      data_store_item: updateParams,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api('/agency/data_store_items/update_for_group_key.json', {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.DATA_STORE_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, result: json }
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))

        return { success: false, result: errors }
      })

    return promise
  }
}

export function destroyItemsForGroupKey(groupKey, digitalTemplateId, token){
  const config = {
    method: 'DELETE',
    body: JSON.stringify({
      digital_template_id: digitalTemplateId,
      group_key: groupKey,
      token,
    }),
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api('/agency/data_store_items/destroy_for_group_key.json', {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        if (json.ids){
          json.ids.forEach((id) => {
            const normalizedJson = normalize({ id }, Schemas.DATA_STORE_ITEM)
            dispatch(deleteEntity(normalizedJson))
          })
        }

        dispatch(deleteSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })
    return promise
  }
}

export function submitResponse(options = {}){
  const {
    digital_template_id, group_key, token, owner_type, owner_id, ...queryOptions
  } = options

  const config = {
    method: 'POST',
    body: JSON.stringify({
      digital_template_id,
      group_key,
      owner_id,
      owner_type,
      token,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api('/agency/data_store_items/submit_response.json', queryOptions, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        dispatch(updateSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForEntities: action.loadedForEntities || state.loadedForEntities,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors,
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors,
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors,
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors,
      }
    default:
      return state
  }
}
